import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, adminApi, fmtAUD } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Check, Plus, X, Truck } from "lucide-react";

const ORDER_STATUSES = ["processing", "hardening", "packed", "shipped", "delivered", "refunded"];

function AdminLogin({ onAuth }) {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/admin/login", { key });
      localStorage.setItem("sv_admin_token", r.data.token);
      onAuth();
    } catch {
      toast.error("Invalid admin key");
    }
    setBusy(false);
  };
  return (
    <div className="pt-40 px-6 md:px-10 min-h-[80svh]" data-testid="admin-login">
      <div className="overline mb-6">Ξ ADMIN — Restricted</div>
      <h1 className="font-display uppercase text-5xl md:text-7xl tracking-tight leading-[0.9] mb-12">Identify.</h1>
      <form onSubmit={submit} className="max-w-md border border-[#1A1A1A] p-8">
        <label className="overline block mb-2">Admin key</label>
        <input type="password" className="field" value={key} onChange={(e) => setKey(e.target.value)} data-testid="admin-key-input" placeholder="••••••••••••" />
        <button disabled={busy} className="btn-vault w-full justify-center mt-6" data-testid="admin-login-submit">
          {busy ? "Verifying…" : "Enter"}
        </button>
      </form>
    </div>
  );
}

function NewProductModal({ onClose, onCreated }) {
  const [f, setF] = useState({
    slug: "", name: "", model: "",
    base_price_aud: "", is_pro_form: false,
    tagline: "", description: "", long_description: "",
    image_url: "", stock: 10, active: true,
    specs_raw: "Chipset: Google Tensor G4\nRAM: 12 GB\nDisplay: 6.3\" OLED 120Hz\nBattery: 4700 mAh\nWeight: 198g",
  });
  const [busy, setBusy] = useState(false);
  const parseSpecs = (raw) =>
    Object.fromEntries(
      raw.split("\n").map((l) => l.split(":").map((s) => s.trim())).filter((p) => p.length === 2 && p[0])
    );
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminApi().post("/admin/products", {
        slug: f.slug,
        name: f.name,
        model: f.model,
        base_price_aud: parseFloat(f.base_price_aud),
        is_pro_form: !!f.is_pro_form,
        tagline: f.tagline,
        description: f.description,
        long_description: f.long_description,
        specs: parseSpecs(f.specs_raw),
        image_url: f.image_url,
        stock: parseInt(f.stock || 0),
        active: f.active,
      });
      toast.success("Device added — 9 variants generated");
      await onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create");
    }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="new-product-modal">
      <div className="bg-[#0A0A0A] border border-[#1A1A1A] w-full max-w-3xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-[#1A1A1A]">
          <div>
            <div className="overline">New Device</div>
            <div className="font-display text-2xl mt-1">Add to catalogue</div>
            <div className="text-[#666] text-xs mt-2">The 9 variants (3 tiers × 3 storage sizes) are generated automatically from the base price + form factor.</div>
          </div>
          <button onClick={onClose} className="text-[#888] hover:text-[#EAEAEA]" data-testid="new-product-close"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="overline block mb-2">Slug (URL)</label><input required className="field" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} placeholder="pixel-10-pro" data-testid="np-slug" /></div>
            <div><label className="overline block mb-2">Model</label><input required className="field" value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} placeholder="Google Pixel 10 Pro" data-testid="np-model" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="overline block mb-2">Display name</label><input required className="field" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Pixel 10 Pro" data-testid="np-name" /></div>
            <div><label className="overline block mb-2">Base price AUD · Degoogled + 256GB</label><input required type="number" step="1" className="field" value={f.base_price_aud} onChange={(e) => setF({ ...f, base_price_aud: e.target.value })} placeholder="1899" data-testid="np-price" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-3 text-[#EAEAEA] text-sm border border-[#1A1A1A] p-3">
              <input type="checkbox" checked={f.is_pro_form} onChange={(e) => setF({ ...f, is_pro_form: e.target.checked })} data-testid="np-proform" />
              <span>Pro / Fold / XL form-factor (bigger storage price deltas)</span>
            </label>
            <div><label className="overline block mb-2">Stock (per SKU)</label><input type="number" className="field" value={f.stock} onChange={(e) => setF({ ...f, stock: e.target.value })} data-testid="np-stock" /></div>
          </div>
          <div><label className="overline block mb-2">Tagline</label><input required className="field" value={f.tagline} onChange={(e) => setF({ ...f, tagline: e.target.value })} data-testid="np-tagline" /></div>
          <div><label className="overline block mb-2">Short description</label><textarea required rows={2} className="field" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="np-desc" /></div>
          <div><label className="overline block mb-2">Long description</label><textarea rows={3} className="field" value={f.long_description} onChange={(e) => setF({ ...f, long_description: e.target.value })} data-testid="np-long" /></div>
          <div><label className="overline block mb-2">Image URL</label><input required className="field" value={f.image_url} onChange={(e) => setF({ ...f, image_url: e.target.value })} data-testid="np-image" /></div>
          <div>
            <label className="overline block mb-2">Specs (one per line — Key: Value)</label>
            <textarea rows={5} className="field font-mono text-xs" value={f.specs_raw} onChange={(e) => setF({ ...f, specs_raw: e.target.value })} data-testid="np-specs" />
          </div>
          <label className="flex items-center gap-3 text-[#EAEAEA] text-sm">
            <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="np-active" />
            <span>Active — visible in shop immediately</span>
          </label>
          <div className="flex gap-3 pt-4 border-t border-[#1A1A1A]">
            <button type="submit" disabled={busy} className="btn-vault flex-1 justify-center" data-testid="np-submit">
              {busy ? "Creating…" : "Create device"}
            </button>
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ProductRow({ p, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const basePrice = p.base_price_aud ?? p.price_aud ?? 0;
  const [form, setForm] = useState({
    name: p.name,
    base_price_aud: basePrice,
    is_pro_form: !!p.is_pro_form,
    tagline: p.tagline,
    stock: p.stock,
    active: p.active,
  });
  useEffect(() => setForm({
    name: p.name,
    base_price_aud: p.base_price_aud ?? p.price_aud ?? 0,
    is_pro_form: !!p.is_pro_form,
    tagline: p.tagline,
    stock: p.stock,
    active: p.active,
  }), [p]);
  const save = async () => {
    await onSave(p.id, {
      name: form.name,
      base_price_aud: parseFloat(form.base_price_aud),
      is_pro_form: !!form.is_pro_form,
      tagline: form.tagline,
      stock: parseInt(form.stock || 0),
      active: form.active,
    });
    setEditing(false);
  };
  const variantCount = (p.variants || []).length;
  return (
    <tr className="border-b border-[#1A1A1A]">
      <td className="p-3">
        {editing ? <input className="field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /> : <div className="text-[#EAEAEA]">{p.name}</div>}
        <div className="overline mt-1 text-[#444]">{p.slug}</div>
      </td>
      <td className="p-3 font-mono text-xs">
        <span className="text-[#EAEAEA]">{variantCount || 9}</span>
        <span className="text-[#666]"> SKUs</span>
      </td>
      <td className="p-3 font-mono">
        {editing ? <input className="field" type="number" value={form.base_price_aud} onChange={(e) => setForm({ ...form, base_price_aud: e.target.value })} /> : <div><div>{fmtAUD(basePrice)}</div><div className="overline text-[#444] mt-1">from Degoogled 256GB</div></div>}
      </td>
      <td className="p-3">
        {editing ? (
          <label className="flex items-center gap-2 text-[#EAEAEA] text-xs">
            <input type="checkbox" checked={form.is_pro_form} onChange={(e) => setForm({ ...form, is_pro_form: e.target.checked })} />
            <span>Pro/Fold/XL</span>
          </label>
        ) : (
          <span className="overline text-[#888]">{p.is_pro_form ? "Pro / Fold / XL" : "Standard"}</span>
        )}
      </td>
      <td className="p-3 font-mono">
        {editing ? <input className="field" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} /> : p.stock}
      </td>
      <td className="p-3">{p.active ? <span className="tier-dot secure" /> : <span className="tier-dot degoogled" />}</td>
      <td className="p-3 text-right">
        {editing ? (
          <div className="flex gap-2 justify-end">
            <button className="btn-ghost" onClick={save} data-testid={`save-product-${p.slug}`}>Save</button>
            <button className="btn-ghost" onClick={() => { setEditing(false); }}>Cancel</button>
          </div>
        ) : (
          <div className="flex gap-2 justify-end">
            <button className="btn-ghost" onClick={() => setEditing(true)} data-testid={`edit-product-${p.slug}`}>Edit</button>
            <button className="text-[#666] hover:text-[#7C3AED]" onClick={() => onDelete(p.id)} data-testid={`delete-product-${p.slug}`}><Trash2 size={16} /></button>
          </div>
        )}
      </td>
    </tr>
  );
}

function OrderRow({ o, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ order_status: o.order_status || "processing", tracking_number: o.tracking_number || "", note: "" });
  const save = async () => {
    await onUpdate(o.session_id, form);
    setEditing(false);
  };
  return (
    <tr className="border-b border-[#1A1A1A] align-top">
      <td className="p-3 font-mono text-xs text-[#888] max-w-[220px] break-all">{o.session_id}</td>
      <td className="p-3">
        <div className="text-[#EAEAEA] text-sm">{o.customer_email || "—"}</div>
        <div className="overline text-[#444]">{o.customer_name || "guest"}</div>
      </td>
      <td className="p-3 text-[#888] text-xs max-w-xs">{(o.items || []).map((i) => `${i.name} x${i.qty}`).join(", ")}</td>
      <td className="p-3">
        <div className="overline">{o.payment_status}</div>
      </td>
      <td className="p-3">
        {editing ? (
          <div className="space-y-2 min-w-[200px]">
            <select className="field" value={form.order_status} onChange={(e) => setForm({ ...form, order_status: e.target.value })} data-testid={`os-select-${o.session_id}`}>
              {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input className="field" placeholder="Tracking #" value={form.tracking_number} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })} data-testid={`os-track-${o.session_id}`} />
            <input className="field" placeholder="Note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid={`os-note-${o.session_id}`} />
          </div>
        ) : (
          <div>
            <div className="overline text-[#EAEAEA]">{o.order_status || "processing"}</div>
            {o.tracking_number && <div className="text-[#888] text-xs mt-1 font-mono">{o.tracking_number}</div>}
          </div>
        )}
      </td>
      <td className="p-3 font-mono text-right">{fmtAUD((o.amount || 0) / 100)}</td>
      <td className="p-3 text-right">
        {editing ? (
          <div className="flex gap-2 justify-end">
            <button className="btn-ghost" onClick={save} data-testid={`os-save-${o.session_id}`}>Save</button>
            <button className="btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        ) : (
          <button className="btn-ghost" onClick={() => setEditing(true)} data-testid={`os-edit-${o.session_id}`}><Truck size={14} /> Update</button>
        )}
      </td>
    </tr>
  );
}

function AdminDash() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({});
  const [products, setProducts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const a = adminApi();
      const [s, p, i, o] = await Promise.all([
        a.get("/admin/stats"),
        a.get("/admin/products"),
        a.get("/admin/inquiries"),
        a.get("/admin/orders"),
      ]);
      setStats(s.data); setProducts(p.data); setInquiries(i.data); setOrders(o.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("sv_admin_token"); nav(0);
      }
    }
  };
  useEffect(() => { load(); }, []);

  const updateProduct = async (id, patch) => { await adminApi().patch(`/admin/products/${id}`, patch); toast.success("Updated"); await load(); };
  const deleteProduct = async (id) => { if (!confirm("Delete this device permanently?")) return; await adminApi().delete(`/admin/products/${id}`); toast.success("Deleted"); await load(); };
  const markRead = async (id) => { await adminApi().patch(`/admin/inquiries/${id}/read`); await load(); };
  const updateOrder = async (session_id, patch) => {
    try {
      await adminApi().patch(`/admin/orders/${session_id}/status`, patch);
      toast.success("Order updated · customer notified");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };
  const logout = () => { localStorage.removeItem("sv_admin_token"); nav("/admin"); nav(0); };

  return (
    <div className="pt-28 pb-24 px-6 md:px-10" data-testid="admin-dashboard">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="overline mb-4">Ξ ADMIN CONSOLE</div>
          <h1 className="font-display uppercase text-4xl md:text-6xl tracking-tight leading-[0.9]">Phantom Phone Ops</h1>
        </div>
        <button onClick={logout} className="btn-ghost" data-testid="admin-logout">Logout →</button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-[#1A1A1A]">
        <div className="border-r border-[#1A1A1A] p-6"><div className="overline">Revenue</div><div className="font-display text-3xl mt-4">{fmtAUD(stats.revenue_aud || 0)}</div></div>
        <div className="border-r border-[#1A1A1A] p-6"><div className="overline">Orders paid</div><div className="font-display text-3xl mt-4">{stats.orders_paid || 0}</div></div>
        <div className="border-r border-[#1A1A1A] p-6"><div className="overline">Pending</div><div className="font-display text-3xl mt-4">{stats.orders_pending || 0}</div></div>
        <div className="border-r border-[#1A1A1A] p-6"><div className="overline">Unread</div><div className="font-display text-3xl mt-4 text-[#7C3AED]">{stats.unread_inquiries || 0}</div></div>
        <div className="p-6"><div className="overline">Active devices</div><div className="font-display text-3xl mt-4">{stats.products_active || 0}</div></div>
      </div>

      <div className="mt-8 flex gap-3 border-b border-[#1A1A1A] pb-3">
        {[["overview", "Overview"], ["products", "Devices"], ["orders", "Orders"], ["inquiries", "Inquiries"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`overline px-3 py-2 border ${tab === k ? "border-[#EAEAEA] text-[#EAEAEA]" : "border-transparent text-[#888]"}`} data-testid={`admin-tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-[#1A1A1A] p-6">
            <div className="overline mb-4">Latest orders</div>
            {orders.slice(0, 5).map((o) => (
              <div key={o.id || o.session_id} className="flex justify-between py-3 border-b border-[#1A1A1A] last:border-0 text-sm">
                <div className="text-[#EAEAEA] truncate max-w-[40%]">{o.customer_email || "guest"}</div>
                <div className="overline">{o.order_status || "processing"}</div>
                <div className="font-mono">{fmtAUD((o.amount || 0) / 100)}</div>
              </div>
            ))}
            {orders.length === 0 && <div className="text-[#666] text-sm">No orders yet</div>}
          </div>
          <div className="border border-[#1A1A1A] p-6">
            <div className="overline mb-4">Latest inquiries</div>
            {inquiries.slice(0, 5).map((i) => (
              <div key={i.id} className="flex justify-between py-3 border-b border-[#1A1A1A] last:border-0 text-sm">
                <div className="text-[#EAEAEA] truncate max-w-[40%]">{i.name}</div>
                <div className="text-[#888] truncate max-w-[50%]">{i.subject}</div>
              </div>
            ))}
            {inquiries.length === 0 && <div className="text-[#666] text-sm">No inquiries yet</div>}
          </div>
        </div>
      )}

      {tab === "products" && (
        <div className="mt-8">
          <div className="flex justify-between items-center mb-4">
            <div className="overline">Catalogue · {products.length} devices</div>
            <button onClick={() => setShowNew(true)} className="btn-vault" data-testid="admin-new-product-btn"><Plus size={14} /> Add device</button>
          </div>
          <div className="border border-[#1A1A1A] overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#1A1A1A]">
                  <th className="p-3 text-left overline">Device</th>
                  <th className="p-3 text-left overline">Variants</th>
                  <th className="p-3 text-left overline">Base price</th>
                  <th className="p-3 text-left overline">Form-factor</th>
                  <th className="p-3 text-left overline">Stock</th>
                  <th className="p-3 text-left overline">Active</th>
                  <th className="p-3 text-right overline">Actions</th>
                </tr>
              </thead>
              <tbody>{products.map((p) => <ProductRow key={p.id} p={p} onSave={updateProduct} onDelete={deleteProduct} />)}</tbody>
            </table>
          </div>
          {showNew && <NewProductModal onClose={() => setShowNew(false)} onCreated={load} />}
        </div>
      )}

      {tab === "orders" && (
        <div className="mt-8 border border-[#1A1A1A] overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#1A1A1A]">
                <th className="p-3 text-left overline">Session</th>
                <th className="p-3 text-left overline">Customer</th>
                <th className="p-3 text-left overline">Items</th>
                <th className="p-3 text-left overline">Payment</th>
                <th className="p-3 text-left overline">Fulfilment</th>
                <th className="p-3 text-right overline">Amount</th>
                <th className="p-3 text-right overline">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => <OrderRow key={o.session_id} o={o} onUpdate={updateOrder} />)}
              {orders.length === 0 && <tr><td colSpan={7} className="text-center text-[#666] py-8">No orders yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "inquiries" && (
        <div className="mt-8 space-y-4">
          {inquiries.map((i) => (
            <div key={i.id} className={`border p-6 ${i.read ? "border-[#1A1A1A]" : "border-[#EAEAEA]"}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="overline mb-1">{i.subject}</div>
                  <div className="text-[#EAEAEA]">{i.name} &lt;{i.email}&gt;</div>
                </div>
                {!i.read && <button onClick={() => markRead(i.id)} className="btn-ghost" data-testid={`mark-read-${i.id}`}><Check size={14} /> Mark read</button>}
              </div>
              <p className="mt-6 text-[#888] whitespace-pre-wrap">{i.message}</p>
            </div>
          ))}
          {inquiries.length === 0 && <div className="text-[#666] text-center py-12">No inquiries yet.</div>}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("sv_admin_token"));
  return authed ? <AdminDash /> : <AdminLogin onAuth={() => setAuthed(true)} />;
}
